import ApiException from '@/exceptions/ApiException';
import type { ComponentPublicInstance } from 'vue';
import BasePageRef from '@/components/common/BasePage';
import type { RouteLocationRaw } from 'vue-router';
import router from '@/router';
import AuthHelper from './AuthHelper';

export default class BaseHelper {
  public static async openMessageDialog(v: ComponentPublicInstance, msg: string) {
    try {
      const res = await BaseHelper.findBase(v).openMessageDialog(msg);
      return res;
    } catch (error) {
      logger.error(error);
    }
    return false;
  }

  /**
   * 確認ダイアログを表示する
   * @param v
   * @param msg 表示メッセージ
   * @param buttonName OKボタン名(左ボタン) デフォルトは "はい" 、変更する場合のみ指定する
   * @param cancelButtonName キャンセルボタン名(右ボタン) デフォルトは "いいえ" 、変更する場合のみしていする
   */
  public static async openConfirmDialog(
    v: ComponentPublicInstance,
    msg: string,
    buttonName?: string,
    cancelButtonName?: string
  ) {
    try {
      const res = await BaseHelper.findBase(v).openConfirmDialog(msg, buttonName, cancelButtonName);
      return res;
    } catch (error) {
      logger.error(error);
    }
    return false;
  }

  /**
   * エラーダイアログを表示する。
   * @param {Vue} v
   * @param {string} msg
   * @param {string} buttonName デフォルトは閉じる、変更する場合のみ指定する
   */
  public static async openErrorDialog(v: ComponentPublicInstance, msg: string, buttonName = '') {
    try {
      const res = await BaseHelper.findBase(v).openErrorDialog(msg, buttonName);
      return res;
    } catch (error) {
      logger.error(error);
    }
    return true;
  }

  public static showKeyboard(v: ComponentPublicInstance) {
    try {
      return BaseHelper.findBase(v).showKeyboard;
    } catch (error) {
      // logger.error(error);
      //Do Nothing
    }
    return false;
  }

  private static findBase(v: ComponentPublicInstance): BasePageRef {
    let vueHierarchy: ComponentPublicInstance | null = v;
    while (vueHierarchy && !vueHierarchy.$refs.$base) {
      vueHierarchy = vueHierarchy.$parent;
    }
    if (vueHierarchy?.$refs.$base) {
      return vueHierarchy.$refs.$base as BasePageRef;
    }
    throw new Error('VUEBASE.NOT_FOUND');
  }

  // eslint-disable-next-line
  public static async handleErrors(v: ComponentPublicInstance | null, err: Error) {
    logger.info(err);
    let location: RouteLocationRaw = { name: 'ServerError' };
    if (err instanceof ApiException) {
      const code = err.code;
      let isAllowIp = null;

      switch (code) {
        case 'AUTH.INVALID_SESSION':
          try {
            AuthHelper.logout();
            isAllowIp = await AuthHelper.ipCheck();
          } catch (err) {
            logger.info(err);
            break;
          }
          if (isAllowIp && isAllowIp.isFixedIp) {
            location = {
              name: 'Start',
              query: { isLibrary: String(isAllowIp.constraintOption!), userId: String(isAllowIp.userId!) },
            };
          } else {
            location = { name: 'Login' };
          }
          break;
        case 'AUTH.AUTO_LOGOUT':
          location = { name: 'SessionError', params: { errorType: 'auto' } };
          break;
        case 'AUTH.FORCED_LOGOUT':
          location = { name: 'SessionError', params: { errorType: 'forced' } };
          break;
        case 'ACCESS.DENIED':
          location = { name: 'NotFoundError' };
          break;
        case 'SYSTEM.UNAVAILABLE':
          location = { name: 'ServerMaintenance' };
          break;
        default:
          location = { name: 'ServerError' };
      }
    }
    if (v) {
      await router.replace(location);
    }
    return location;
  }
}
